import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

interface Props {
  identity: string;
  releaseUrl: '/hijack/release/' | '/support/return/';
  csrfToken: string;
  pending?: boolean;
  onRelease?: (event: FormEvent<HTMLFormElement>) => void;
}

export function ImpersonationBanner({ identity, releaseUrl, csrfToken, pending = false, onRelease }: Props) {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const busy = pending || submitting;
  return <Alert severity="warning" variant="filled" role="region" aria-label="Impersonation active"
    sx={{ mb: 3, position: 'sticky', top: 64, zIndex: 1, minWidth: 0, color: 'warning.contrastText',
      '& .MuiAlert-icon': { color: 'inherit' },
      '& .MuiAlert-message': { width: '100%', minWidth: 0 } }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }}>
      <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
        <Typography fontWeight="bold">Impersonation active</Typography>
        <Typography variant="body2">Working as {identity}. Actions use this account’s permissions.</Typography>
      </Box>
      <Box component="form" action={releaseUrl} method="post" onSubmit={(event: FormEvent<HTMLFormElement>) => {
        onRelease?.(event);
        if (!event.defaultPrevented) setSubmitting(true);
      }}>
        <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />
        <input type="hidden" name="next" value="/support/" />
        <Button type="submit" variant="outlined" color="inherit" disabled={busy || !csrfToken}
          sx={{ whiteSpace: 'normal', borderColor: 'currentColor', '&:hover': { borderColor: 'currentColor' },
            '&:focus-visible, &.Mui-focusVisible': { outlineColor: 'currentColor' } }}>
          {busy ? 'Ending impersonation…' : 'End impersonation'}</Button>
        {!csrfToken && <Typography variant="body2">Reload to restore the secure end action.</Typography>}
      </Box>
    </Stack>
  </Alert>;
}
